import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOJsonLd from '@/components/feature/SEOJsonLd';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const pageTitle = `${category.name}韩漫 - 在线韩国漫画 | 好看的${category.name}韩漫免费在线观看`;
  const pageDesc = `韩漫网站提供${category.name}类韩国漫画免费在线观看，精选海量高质量${category.name}韩漫资源，每日更新，画质精美，让您尽情享受阅读乐趣。`;

  const pageJsonLd = {
    name: pageTitle,
    description: pageDesc,
    url: `http://guyunqutang.com/category/${slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: 'http://guyunqutang.com/',
    },
  };

  return (
    <>
      <SEOJsonLd type="WebPage" data={pageJsonLd} />
      <header>
        <Navbar />
      </header>
      <main>
        <section className="pt-24 pb-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />

            <div className="flex flex-col lg:flex-row lg:items-center gap-6 mt-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-background-100`}>
                    <i className={`${category.icon} text-2xl text-foreground-700`}></i>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                    {category.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-600 max-w-xl leading-relaxed">
                  浏览全部{category.name}类韩漫，共 {categoryComics.length} 部作品。每日更新，精彩不断。
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                      cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer"
                >
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full mb-2 ${
                      comic.status === '连载中'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-600 text-background-100'
                    }`}>
                      {comic.status}
                    </span>
                    <h3 className="text-lg font-heading font-bold text-background-50 mb-1">{comic.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-background-300">
                      <span>{comic.author}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        {comic.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
              没找到喜欢的？
            </h2>
            <p className="text-sm text-foreground-600 mb-6">
              浏览其他分类，发现更多精彩漫画
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.filter(c => c.slug !== slug).slice(0, 4).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-2 bg-background-50 text-foreground-700 text-sm font-medium px-5 py-2.5 rounded-full border border-background-200/60 hover:border-primary-300 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className={cat.icon}></i>
                  {cat.name}漫画
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}